/** Same-running-tab delivery facts, never credentials or durable authority. */
const workspaces = new Map();
const preparedWorkspaces = new Set();
const actions = new Map();
const listeners = new Set();
const deferredUrls = new Set();
let nextIdentity = 0;
let storeRevision = 0;

const notify = () => {
  storeRevision += 1;
  listeners.forEach((listener) => listener());
};
export const subscribeDeliveryWorkspaces = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const getDeliveryWorkspaceRevision = () => storeRevision;

export const deliveryPrincipalKey = (binding) => {
  const { user, source } = binding || {};
  if (typeof user?.tenant_id !== 'string' || !user.tenant_id.trim()
      || typeof user?.id !== 'string' || !user.id.trim()
      || !['cloud', 'offline'].includes(source)) return null;
  return JSON.stringify([user.tenant_id, user.id, source]);
};
const orderKey = (principalKey, orderId) => JSON.stringify([principalKey, orderId]);
const workspaceKey = (principalKey, orderId, kind) => JSON.stringify([principalKey, orderId, kind]);
const validKey = (principalKey, orderId, kind) => (
  typeof principalKey === 'string' && Boolean(principalKey)
  && typeof orderId === 'string' && Boolean(orderId)
  && ['confirmation', 'amendment', 'failure'].includes(kind)
);

// Plain containers are copied and frozen. File/Blob values are immutable
// browser objects. Reject secret-shaped properties at this custody boundary.
const copyFacts = (value) => {
  if (Array.isArray(value)) return Object.freeze(value.map(copyFacts));
  if (value && Object.getPrototypeOf(value) === Object.prototype) {
    const result = {};
    Object.entries(value).forEach(([key, item]) => {
      if (/^(token|authorization|password|credentials|accessToken|refreshToken)$/i.test(key)) {
        throw new Error('Credentials cannot be retained in delivery work.');
      }
      result[key] = copyFacts(item);
    });
    return Object.freeze(result);
  }
  return value;
};
const urls = (workspace) => {
  const result = new Set();
  const collect = (fields) => {
    fields?.photos?.forEach((photo) => { if (photo.preview) result.add(photo.preview); });
    if (fields?.video?.preview) result.add(fields.video.preview);
  };
  collect(workspace?.fields);
  collect(workspace?.attempt?.fields);
  return result;
};
const retireUrls = (previous, next) => {
  const retained = urls(next);
  urls(previous).forEach((url) => { if (!retained.has(url)) deferredUrls.add(url); });
};
const collectUnusedUrls = () => {
  const retained = new Set();
  workspaces.forEach((workspace) => urls(workspace).forEach((url) => retained.add(url)));
  actions.forEach((lease) => lease.urls.forEach((url) => retained.add(url)));
  deferredUrls.forEach((url) => {
    if (!retained.has(url)) {
      URL.revokeObjectURL(url);
      deferredUrls.delete(url);
    }
  });
};

export const getDeliveryWorkspace = (principalKey, orderId, kind) => (
  workspaces.get(workspaceKey(principalKey, orderId, kind)) || null
);
export const listDeliveryWorkspaces = (principalKey, orderId) => (
  [...workspaces.values()].filter((workspace) => workspace.principalKey === principalKey && (orderId === undefined || workspace.orderId === orderId))
);
export const createDeliveryWorkspace = (principalKey, orderId, kind, fields) => {
  if (!validKey(principalKey, orderId, kind)) return null;
  const key = workspaceKey(principalKey, orderId, kind);
  if (workspaces.has(key)) return workspaces.get(key);
  const workspace = Object.freeze({
    key, id: ++nextIdentity, revision: 0, principalKey, orderId, kind,
    fields: copyFacts(fields), initialFields: copyFacts(fields), checkpoint: null, attempt: null, savedMediaRemoval: null, photoDraftRevision: null,
  });
  workspaces.set(key, workspace);
  notify();
  return workspace;
};
export const getDeliveryAction = (workspace) => (
  workspace ? actions.get(orderKey(workspace.principalKey, workspace.orderId)) || null : null
);

// Merely viewing a form (including automatic GPS/time initialization) is not
// unfinished work. Never discard an operation whose outcome still needs review.
export const hasDeliveryWorkspaceWork = (workspace) => {
  if (!workspace) return false;
  if (getDeliveryAction(workspace) || workspace.attempt || workspace.savedMediaRemoval) return true;
  const { fields, initialFields: baseline, kind, checkpoint } = workspace;
  if (!baseline) return true; // Preserve older in-memory work without a baseline.
  if (fields.photos?.length || fields.video || fields.signatureData) return true;
  if (kind === 'failure') {
    return fields.reason !== baseline.reason || fields.details !== baseline.details;
  }
  if (kind === 'confirmation' && !fields.gpsLocation && !fields.deliveryTime && !fields.deliveryTimeIso
      && !['receivedBy', 'receivedByRelationship', 'notes', 'failureReason', 'failureDetails'].some((key) => fields[key]?.trim())) return false;
  if (fields.failureReason || fields.failureDetails) return true;
  const acknowledged = kind === 'amendment' && checkpoint?.acknowledgedText;
  const textFields = ['receivedBy', 'receivedByRelationship', 'notes'];
  const savedKeys = ['received_by', 'received_by_relationship', 'notes'];
  if (textFields.some((name, index) => acknowledged
    ? fields[name]?.trim() !== acknowledged[savedKeys[index]]
    : fields[name] !== baseline[name])) return true;
  return kind === 'confirmation' && fields.deliveryTime !== baseline.deliveryTime;
};

// Private lineage survives field revisions, never a principal switch or a new
// workspace. Only a boolean crosses the legacy admission boundary.
export const markPreparedDeliveryWorkspace = (workspace) => {
  if (!workspace || workspaces.get(workspace.key) !== workspace) return false;
  preparedWorkspaces.add(workspace.id);
  return true;
};
export const hasPreparedDeliveryWorkspace = (orderId) => {
  for (const workspace of workspaces.values()) {
    if (workspace.orderId !== orderId) continue;
    const recognized = preparedWorkspaces.has(workspace.id) || !!workspace.photoDraftRevision
      || !!workspace.attempt?.captureId || workspace.fields.photos?.some((photo) => !!photo.prepared);
    if (recognized && hasDeliveryWorkspaceWork(workspace)) return true;
  }
  return false;
};
const mayChange = (expected, lease) => (
  expected && workspaces.get(expected.key) === expected
  && (!getDeliveryAction(expected) || (getDeliveryAction(expected) === lease && lease.workspaceId === expected.id))
);
export const updateDeliveryWorkspace = (expected, patch, lease) => {
  if (!mayChange(expected, lease)) return null;
  const next = Object.freeze({
    ...expected,
    fields: Object.hasOwn(patch, 'fields') ? copyFacts(patch.fields) : expected.fields,
    checkpoint: Object.hasOwn(patch, 'checkpoint') ? copyFacts(patch.checkpoint) : expected.checkpoint,
    attempt: Object.hasOwn(patch, 'attempt') ? copyFacts(patch.attempt) : expected.attempt,
    savedMediaRemoval: Object.hasOwn(patch, 'savedMediaRemoval') ? copyFacts(patch.savedMediaRemoval) : expected.savedMediaRemoval,
    photoDraftRevision: Object.hasOwn(patch, 'photoDraftRevision') ? patch.photoDraftRevision : expected.photoDraftRevision,
    revision: expected.revision + 1,
  });
  workspaces.set(expected.key, next);
  retireUrls(expected, next);
  collectUnusedUrls();
  notify();
  return next;
};
export const clearDeliveryWorkspace = (expected, lease) => {
  if (!mayChange(expected, lease)) return false;
  workspaces.delete(expected.key);
  preparedWorkspaces.delete(expected.id);
  retireUrls(expected, null);
  collectUnusedUrls();
  notify();
  return true;
};
export const acquireDeliveryAction = (expected, action) => {
  if (!expected || workspaces.get(expected.key) !== expected || getDeliveryAction(expected)) return null;
  const key = orderKey(expected.principalKey, expected.orderId);
  const lease = Object.freeze({
    id: ++nextIdentity, action, key, workspaceId: expected.id,
    urls: Object.freeze([...urls(expected)]),
  });
  actions.set(key, lease);
  notify();
  return lease;
};
export const releaseDeliveryAction = (lease) => {
  if (!lease || actions.get(lease.key) !== lease) return false;
  actions.delete(lease.key);
  collectUnusedUrls();
  notify();
  return true;
};
